// <s-product-recommendations> — loads Shopify product recommendations for the
// current product once the region nears the viewport, then fills a Liquid-rendered
// card <template> for each result. The region stays hidden if nothing comes back.
// Attributes (written by Liquid): data-url (routes.product_recommendations_url),
// data-product-id, data-limit, data-intent, data-locale, data-currency.
class SProductRecommendations extends HTMLElement {
  connectedCallback() {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    const { url, productId, limit = '4', intent = 'related', locale = 'en-GB', currency = 'GBP' } = this.dataset;
    const list = this.querySelector('[data-recommendations-list]');
    const template = this.querySelector('template');
    if (!url || !productId || !list || !template) return;

    try {
      const params = new URLSearchParams({ product_id: productId, limit, intent });
      const response = await fetch(`${url}.json?${params}`);
      if (!response.ok) return;
      const { products = [] } = await response.json();
      if (!products.length) return;

      const money = new Intl.NumberFormat(locale, { style: 'currency', currency });
      for (const product of products) {
        const card = template.content.cloneNode(true);
        card.querySelectorAll('[data-rec-link]').forEach((link) => {
          link.href = product.url;
        });
        const title = card.querySelector('[data-rec-title]');
        if (title) title.textContent = product.title;
        const price = card.querySelector('[data-rec-price]');
        if (price) price.textContent = money.format(product.price / 100);
        const image = card.querySelector('[data-rec-image]');
        const src = product.featured_image;
        if (image && src) {
          const base = src.startsWith('//') ? `https:${src}` : src;
          const sized = (width) => `${base}${base.includes('?') ? '&' : '?'}width=${width}`;
          image.src = sized(600);
          image.srcset = [300, 450, 600, 900].map((width) => `${sized(width)} ${width}w`).join(', ');
          image.alt = product.title;
        } else {
          image?.remove();
        }
        list.append(card);
      }
      this.hidden = false;
    } catch {
      // Network failure: leave the region hidden rather than show a broken grid.
    }
  }
}

if (!customElements.get('s-product-recommendations')) {
  customElements.define('s-product-recommendations', SProductRecommendations);
}
